import { BUTTON_SIZE } from "./types";
import type { ResponsiveSizes } from "./types";
import { buttonSize } from "./constants";

type SizedBreakpoint = keyof ResponsiveSizes;

/** Prefixed size classes — full literals so Tailwind can detect them. */
const buttonSizeAtSm: Record<BUTTON_SIZE, string> = {
  [BUTTON_SIZE.XLARGE]: "sm:h-12 sm:px-4 sm:rounded-8 sm:button-md",
  [BUTTON_SIZE.LARGE]: "sm:h-10 sm:rounded-8 sm:button-md sm:px-3",
  [BUTTON_SIZE.MEDIUM]: "sm:h-8 sm:px-2 sm:rounded-6 sm:button-sm",
  [BUTTON_SIZE.SMALL]: "sm:h-6 sm:px-1 sm:rounded-4 sm:button-sm",
};

const buttonSizeAtMd: Record<BUTTON_SIZE, string> = {
  [BUTTON_SIZE.XLARGE]: "md:h-12 md:px-4 md:rounded-8 md:button-md",
  [BUTTON_SIZE.LARGE]: "md:h-10 md:rounded-8 md:button-md md:px-3",
  [BUTTON_SIZE.MEDIUM]: "md:h-8 md:px-2 md:rounded-6 md:button-sm",
  [BUTTON_SIZE.SMALL]: "md:h-6 md:px-1 md:rounded-4 md:button-sm",
};

const buttonSizeAtLg: Record<BUTTON_SIZE, string> = {
  [BUTTON_SIZE.XLARGE]: "lg:h-12 lg:px-4 lg:rounded-8 lg:button-md",
  [BUTTON_SIZE.LARGE]: "lg:h-10 lg:rounded-8 lg:button-md lg:px-3",
  [BUTTON_SIZE.MEDIUM]: "lg:h-8 lg:px-2 lg:rounded-6 lg:button-sm",
  [BUTTON_SIZE.SMALL]: "lg:h-6 lg:px-1 lg:rounded-4 lg:button-sm",
};

const buttonSizeAtXl: Record<BUTTON_SIZE, string> = {
  [BUTTON_SIZE.XLARGE]: "xl:h-12 xl:px-4 xl:rounded-8 xl:button-md",
  [BUTTON_SIZE.LARGE]: "xl:h-10 xl:rounded-8 xl:button-md xl:px-3",
  [BUTTON_SIZE.MEDIUM]: "xl:h-8 xl:px-2 xl:rounded-6 xl:button-sm",
  [BUTTON_SIZE.SMALL]: "xl:h-6 xl:px-1 xl:rounded-4 xl:button-sm",
};

const buttonSizeAt2xl: Record<BUTTON_SIZE, string> = {
  [BUTTON_SIZE.XLARGE]: "2xl:h-12 2xl:px-4 2xl:rounded-8 2xl:button-md",
  [BUTTON_SIZE.LARGE]: "2xl:h-10 2xl:rounded-8 2xl:button-md 2xl:px-3",
  [BUTTON_SIZE.MEDIUM]: "2xl:h-8 2xl:px-2 2xl:rounded-6 2xl:button-sm",
  [BUTTON_SIZE.SMALL]: "2xl:h-6 2xl:px-1 2xl:rounded-4 2xl:button-sm",
};

const responsiveSizeMaps: Record<
  Exclude<SizedBreakpoint, "default">,
  Record<BUTTON_SIZE, string>
> = {
  sm: buttonSizeAtSm,
  md: buttonSizeAtMd,
  lg: buttonSizeAtLg,
  xl: buttonSizeAtXl,
  "2xl": buttonSizeAt2xl,
};

export function resolvePrimaryButtonSize(
  size: BUTTON_SIZE,
  sizes?: ResponsiveSizes
): BUTTON_SIZE {
  return sizes?.default ?? size;
}

export function resolveButtonSizeClassNames(
  size: BUTTON_SIZE,
  sizes?: ResponsiveSizes
): string {
  if (!sizes || Object.keys(sizes).length === 0) {
    return buttonSize[size];
  }

  const classNames: string[] = [];
  const defaultSize = sizes.default ?? size;
  classNames.push(buttonSize[defaultSize]);

  (Object.keys(responsiveSizeMaps) as Exclude<SizedBreakpoint, "default">[]).forEach(
    (breakpoint) => {
      const breakpointSize = sizes[breakpoint];
      if (breakpointSize) {
        classNames.push(responsiveSizeMaps[breakpoint][breakpointSize]);
      }
    }
  );

  return classNames.join(" ");
}

export const spinnerSizeByButtonSize: Record<BUTTON_SIZE, number> = {
  [BUTTON_SIZE.XLARGE]: 20,
  [BUTTON_SIZE.LARGE]: 18,
  [BUTTON_SIZE.MEDIUM]: 16,
  [BUTTON_SIZE.SMALL]: 14,
};
